/**
 * Single owner: items row → MatchListingInput for pull + push match paths.
 *
 * `items` has brand/model columns; size lives in attributes JSON (not a column).
 * Prefer this over duplicating .select() strings in find/notify modules.
 */

import type { Database } from "@/lib/database.types";
import type { MatchListingInput } from "./matchTypes";
import { normalizePreferenceAttrs } from "./preferenceAttrs";

export type MatchItemRow = Pick<
  Database["public"]["Tables"]["items"]["Row"],
  | "id"
  | "title"
  | "price"
  | "category"
  | "subcategory"
  | "location"
  | "location_state_id"
  | "location_lga_id"
  | "location_neighborhood_id"
  | "created_at"
  | "seller_availability_confirmed_at"
  | "status"
  | "attributes"
  | "brand"
  | "model"
>;

export type MatchItemRowWithSeller = MatchItemRow &
  Pick<Database["public"]["Tables"]["items"]["Row"], "seller_id">;

/** Columns that exist on `items` and are needed for match scoring. */
export const ITEM_MATCH_LISTING_SELECT =
  "id,title,price,category,subcategory,location,location_state_id,location_lga_id,location_neighborhood_id,created_at,seller_availability_confirmed_at,status,attributes,brand,model" as const;

export const ITEM_MATCH_LISTING_SELECT_WITH_SELLER =
  `${ITEM_MATCH_LISTING_SELECT},seller_id` as const;

function sizeFromAttributes(attributes: unknown): string | null {
  const attrs = normalizePreferenceAttrs(attributes ?? {});
  const size = attrs.size;
  if (typeof size === "string" && size.trim()) return size.trim();
  if (typeof size === "number" && Number.isFinite(size)) return String(size);
  return null;
}

/**
 * Map a typed items row into match-engine listing input.
 * Size comes from attributes JSON when present.
 */
export function itemRowToMatchListing(row: MatchItemRow): MatchListingInput {
  return {
    id: row.id,
    title: row.title || "Listing",
    price: Number(row.price) || 0,
    category: row.category || "",
    subcategory: row.subcategory || "",
    location: row.location || "",
    locationStateId: row.location_state_id,
    locationLgaId: row.location_lga_id,
    locationNeighborhoodId: row.location_neighborhood_id,
    createdAt: row.created_at,
    sellerAvailabilityConfirmedAt: row.seller_availability_confirmed_at,
    attributes: row.attributes,
    brand: row.brand,
    size: sizeFromAttributes(row.attributes),
    model: row.model,
  };
}

export function itemRowToMatchListingWithSeller(
  row: MatchItemRowWithSeller,
): MatchListingInput & { sellerId: string } {
  return {
    ...itemRowToMatchListing(row),
    sellerId: row.seller_id,
  };
}
